export type KrSidePanelRequest = {
  krId: string;
  source?: string;
  /** Element focused again when the panel closes. */
  opener?: HTMLElement | null;
  /** Opened from inside the federated OKR Dialog: stack the panel above it. */
  aboveModal?: boolean;
  /** Legacy navigation, run when the remote fails to load or the flag is off. */
  fallback?: () => void;
};

type Handler = (request: KrSidePanelRequest) => void;

declare global {
  interface Window {
    openFederatedKrSidePanel?: (
      krId: string | number,
      options?: Omit<KrSidePanelRequest, 'krId'>,
    ) => void;
  }
}

let handler: Handler | undefined;
let closer: (() => void) | undefined;
// ponytail: unbounded queue, no timeout fallback if no host ever registers; iteration 2.
const queue: KrSidePanelRequest[] = [];

/** Org flag `use_federated_kr_side_panel_by_default`, read per call so a late wbConfig counts. */
export const isKrSidePanelEnabled = () => Boolean(window.wbConfig?.isFederatedKrSidePanelDefault);

/** Same URL KrView builds for its own KR links; the legacy full-page destination. */
export const krDetailsPageHref = (krId: string) =>
  `${window.location.origin}/wb/goal/krDetails?id=${encodeURIComponent(krId)}`;

/** The host registers here; requests made before it mounts are replayed in order. */
export const registerKrSidePanelHandler = (next: Handler, close?: () => void) => {
  handler = next;
  closer = close;
  queue.splice(0).forEach(next);
  return () => {
    if (handler === next) {
      handler = undefined;
      closer = undefined;
    }
  };
};

/** Starts the exit of the open panel, if any. */
export const closeKrSidePanel = () => closer?.();

export const openFederatedKrSidePanel = (
  krId: string | number,
  options: Omit<KrSidePanelRequest, 'krId'> = {},
) => {
  const request = {
    opener: document.activeElement as HTMLElement | null,
    ...options,
    krId: String(krId),
  };
  if (handler) handler(request);
  else queue.push(request);
};

window.openFederatedKrSidePanel = openFederatedKrSidePanel;
